/**
 * FikoAktiviteCep - Cep Açma (Pocket Milling) Aktivitesi
 *
 * Profesyonel CAM cep açma sistemi
 * Mastercam/Fusion360 standartlarında parametreler
 *
 * Özellikler:
 * - Offset/Spiral/Raster temizleme stratejileri
 * - Ada (Island) desteği
 * - Roughing + Finishing ayrımı
 * - 5 farklı giriş yöntemi (Plunge, Rampa, Helisel, Yay, Ön-delme)
 * - Dinamik frezeleme desteği
 * - Köşe optimizasyonu
 *
 * @author FikoYT CAM Sistemi
 * @version 2.0.0
 */

import * as THREE from 'three';
import { FikoSahne } from '../../../FikoSahne';
import { FikoObje3D } from '../../../FikoObje3D';
import { FikoVektor3D } from '../../../FikoVektor3d';
import { generateAdaptiveToolpath, AdaptiveParams, ToolpathSegment, halkalariSpiraleBagla, segmentleriYollara } from '../../strategies';
import { occtKonturOffsetSync, occtKonturOffsetSegSync } from '../../brep/occt-cam-kontur';
import { FikoVektorTakimYol3D } from '../../../FikoThreeCam/FikoVektorTakimYol3d';
import { FikoTakimFrezeleme } from '../../../FikoThreeCam/FikoTakimFrezeleme';
import { FikoTakimYolu } from '../../../FikoThreeCam/FikoTakimYolu';
import { offsetHat } from '../../../Fiko2dPathSihirbaz';

// ═══════════════════════════════════════════════════════════════════════════
// FikoAktiviteCep - Pocket Milling (Cep Açma) Aktivitesi
// ═══════════════════════════════════════════════════════════════════════════

export class FikoAktiviteCep extends FikoObje3D {

    // ═══════════════════════════════════════════════════════════════════
    // SINIF SABİTLERİ
    // ═══════════════════════════════════════════════════════════════════

    static SAYAC = 1;
    override type: 'FikoAktiviteCep' = 'FikoAktiviteCep';
    readonly isGroup: true = true;

    // ═══════════════════════════════════════════════════════════════════
    // GEOMETRİ VERİLERİ
    // ═══════════════════════════════════════════════════════════════════

    /** Ana cep sınır noktaları (kapalı poligon) */
    cepSinirNoktalar: FikoVektor3D[] = [];

    /** Ada (Island) noktaları - işlenMEyecek bölgeler */
    adalar: FikoVektor3D[][] = [];

    /** Hesaplanan offset yolları */
    offsetYollari: FikoVektor3D[][] = [];
    /** offsetYollari ile paralel feed çarpanı (nokta başına). Sadece Adaptive doldurur; boş=sabit feed. */
    private offsetYollariHiz: number[][] = [];

    /** OCCT exact offset referansı (dialog OCCT yüz seçiminde set eder). */
    public occtGrup: any = null;
    public occtFaceId: number | null = null;
    public occtMatris: THREE.Matrix4 | null = null;

    /** offsetYollari ile paralel yay bilgisi (G02/G03). null=linear; {merkez,cw}=bu noktaya yay hareketi. */
    private offsetYollariArk: ({ merkez: { x: number; y: number; z: number }; cw: boolean } | null)[][] = [];
    /** G02/G03 yay çıktısı (SEÇENEK, default G01 güvenli). Sadece OCCT exact offset'te. */
    public g02g03Aktif = false;

    /** Soğutma sıvısı (flood coolant M08/M09). */
    public sogutmaAktif = true;

    /** İş koordinat sistemi (work offset G54-G59). */
    public workOffset = 'G54';

    /** Takım yolu noktaları */
    takimYoluNoktalar: FikoVektorTakimYol3D[] = [];

    /** Takım yolu objesi */
    takimYolu: FikoTakimYolu;

    /** Takım objesi */
    takim: FikoTakimFrezeleme;

    // ═══════════════════════════════════════════════════════════════════
    // Z SEVİYELERİ (HEIGHTS) - Profesyonel CAM Standardı
    // ═══════════════════════════════════════════════════════════════════

    /** Üst Z seviyesi - Top of Stock (malzeme üstü) */
    ustZ: number = 0;

    /** Alt Z seviyesi - Bottom (cep tabanı) */
    altZ: number = -10;

    /**
     * Clearance Height - Güvenli hareket yüksekliği
     * Tüm rapid hareketlerde kullanılır
     * Fixtures ve clamps üzerinde güvenli geçiş sağlar
     */
    clearanceHeight: number = 25;

    /**
     * Retract Height - Geri çekme yüksekliği
     * İşlemler arası geçişlerde kullanılır
     * Clearance'dan düşük ama güvenli
     */
    retractHeight: number = 5;

    /**
     * Feed Height - İlerleme başlangıç yüksekliği
     * Feed rate'in başladığı Z seviyesi
     * Rapid'den feed'e geçiş noktası
     */
    feedHeight: number = 2;

    /** @deprecated Eski güvenliZ - clearanceHeight kullan */
    get guvenliZ(): number { return this.clearanceHeight; }
    set guvenliZ(val: number) { this.clearanceHeight = val; }

    /** @deprecated Eski araGecisZ - retractHeight kullan */
    get araGecisZ(): number { return this.retractHeight; }
    set araGecisZ(val: number) { this.retractHeight = val; }

    /** Z hatve (her pas derinliği) - Stepdown */
    zHatve: number = 2;

    // ═══════════════════════════════════════════════════════════════════
    // TEMİZLEME STRATEJİSİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Temizleme stratejisi
     * - Offset: İçten dışa veya dıştan içe paralel yollar (en yaygın)
     * - Spiral: Sürekli spiral hareket (daha az kaldırma)
     * - Raster: Paralel çizgiler (zik-zak)
     * - Adaptive: Dinamik frezeleme (yük dengeleme)
     */
    temizlemeStratejisi: 'Offset' | 'Spiral' | 'Raster' | 'Adaptive' = 'Offset';

    /**
     * Offset yönü
     * - IctenDisa: Merkezden başla, dışa doğru genişle (Inside-Out)
     * - DistanIce: Dıştan başla, merkeze doğru daralt (Outside-In)
     */
    offsetYonu: 'IctenDisa' | 'DistanIce' = 'IctenDisa';

    /**
     * Yanal hatve (step-over)
     * Takım çapının yüzdesi olarak (örn: 0.4 = %40)
     */
    yanalHatveOrani: number = 0.4;

    /** Yanal hatve (mm cinsinden hesaplanmış) */
    get yanalHatve(): number {
        return this.takim.cap * this.yanalHatveOrani;
    }

    /**
     * Raster açısı (Raster stratejisi için)
     * 0 = X ekseni boyunca, 90 = Y ekseni boyunca
     */
    rasterAcisi: number = 0;

    /** Raster çift yön (zik-zak mı tek yön mü) */
    rasterCiftYon: boolean = true;

    /**
     * Optimal Load / Radial Engagement (Adaptive/Trochoidal strateji için)
     * Takım çapının yüzdesi olarak maksimum radial angajman
     * Tipik değer: 0.10 - 0.25 (%10 - %25)
     * Sabit talaş yükü sağlar, takım ömrünü artırır
     */
    optimalYuk: number = 0.15;

    /** Trochoidal hareket aktif mi (Adaptive strateji için) */
    trochoidalEtkin: boolean = false;

    /** Trochoidal hareket çapı (mm) */
    trochoidalCap: number = 0;

    // ═══════════════════════════════════════════════════════════════════
    // FREZELEME YÖNÜ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Frezeleme yönü
     * - Tirmanma (Climb): Takım dönüş yönünde ilerler (daha iyi yüzey)
     * - Konvansiyonel (Conventional): Takım dönüşüne karşı ilerler
     */
    frezeYonu: 'Tirmanma' | 'Konvansiyonel' = 'Tirmanma';

    // ═══════════════════════════════════════════════════════════════════
    // GİRİŞ YÖNTEMİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Giriş yöntemi
     * - Dikey: Doğrudan dalma (sadece yumuşak malzeme)
     * - Rampa: Açılı dalma (orta sertlik)
     * - Helisel: Spiral dalma (en güvenli)
     * - Yay: Arc giriş (finish için ideal)
     * - OnDelme: Önceden delinmiş delikten giriş
     */
    girisYontemi: 'Dikey' | 'Rampa' | 'Helisel' | 'Yay' | 'OnDelme' = 'Helisel';

    /** Rampa açısı (derece) */
    rampaAcisi: number = 3;

    /** Rampa minimum uzunluk (mm) */
    rampaMinUzunluk: number = 10;

    /** Helisel çap (mm) */
    heliselCap: number = 10;

    /** Helisel adım (mm/tur) */
    heliselAdim: number = 1;

    /** Yay radius (mm) */
    yayRadius: number = 5;

    /** Yay açısı (derece) */
    yayAci: number = 90;

    /** Ön-delme delik koordinatı */
    onDelmeNokta: FikoVektor3D | null = null;

    // ═══════════════════════════════════════════════════════════════════
    // ÇIKIŞ YÖNTEMİ
    // ═══════════════════════════════════════════════════════════════════

    /** Çıkış yöntemi */
    cikisYontemi: 'Dikey' | 'Rampa' | 'Yay' = 'Dikey';

    /** Çıkış rampa açısı */
    cikisRampaAcisi: number = 5;

    /** Çıkış yay radius */
    cikisYayRadius: number = 5;

    // ═══════════════════════════════════════════════════════════════════
    // HIZ PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    /** İlerleme hızı (mm/dk) - ana işleme */
    ilerleme: number = 1000;

    /** Dalma ilerleme (mm/dk) */
    ilerlemeDalma: number = 300;

    /** Rapid hız (mm/dk) */
    hizRapid: number = 10000;

    /** Spindle devri (rpm) */
    devir: number = 8000;

    // ═══════════════════════════════════════════════════════════════════
    // FİNİŞ PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    /** Finiş geçişi aktif mi */
    finisEtkin: boolean = true;

    /**
     * Stock to Leave - Duvar (Wall) payı (mm)
     * Roughing'de yan duvarlarda bırakılan finiş payı
     * Profesyonel CAM standardı: 0.2-0.5mm
     */
    stockToLeaveWall: number = 0.3;

    /**
     * Stock to Leave - Taban (Floor) payı (mm)
     * Roughing'de tabanda bırakılan finiş payı
     * Genellikle duvardan daha az (0.1-0.2mm)
     */
    stockToLeaveFloor: number = 0.1;

    /** @deprecated Eski finisPay - stockToLeaveWall kullan */
    get finisPay(): number { return this.stockToLeaveWall; }
    set finisPay(val: number) { this.stockToLeaveWall = val; }

    /** Finiş geçiş sayısı */
    finisGecisSayisi: number = 1;

    /** Finiş ilerleme (mm/dk) */
    finisIlerleme: number = 800;

    /** Finiş devir (rpm) */
    finisDevir: number = 10000;

    /** Finiş ayrı Z seviyesinde mi */
    finisAyriZ: boolean = false;

    /** Finiş Z seviyesi */
    finisZ: number = -10;

    // ═══════════════════════════════════════════════════════════════════
    // TABAN (FLOOR) İŞLEME
    // ═══════════════════════════════════════════════════════════════════

    /** Taban işleme aktif mi */
    tabanIsle: boolean = true;

    /** Taban finiş payı (mm) */
    tabanFinisPay: number = 0.1;

    /** Taban stratejisi */
    tabanStratejisi: 'Offset' | 'Raster' = 'Offset';

    // ═══════════════════════════════════════════════════════════════════
    // KÖŞE OPTİMİZASYONU
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Köşe işleme modu
     * - Keskin: Tam köşe (roll around)
     * - Dairesel: Takım çapına göre radius
     * - Yakinsal: Minimum radius ile yaklaşım
     */
    koseIsleme: 'Keskin' | 'Dairesel' | 'Yakinsal' = 'Dairesel';

    /** Köşe yavaşlama aktif mi */
    koseYavaslamaEtkin: boolean = true;

    /** Köşe yavaşlama oranı (%) */
    koseYavaslamaOrani: number = 50;

    /** Minimum köşe açısı (derece) - bundan keskin köşelerde yavaşla */
    koseMinAci: number = 90;

    // ═══════════════════════════════════════════════════════════════════
    // ADA (ISLAND) PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    /** Ada işleme aktif mi */
    adaIsleEtkin: boolean = true;

    /** Ada etrafı offset (mm) */
    adaOffset: number = 0;

    /** Ada duvar finiş */
    adaDuvarFinis: boolean = true;

    // ═══════════════════════════════════════════════════════════════════
    // GEÇİŞ PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Offset yolları arası geçiş tipi
     * - Direkt: Düz çizgi bağlantı
     * - Yumusak: Smooth arc bağlantı
     * - ZKaldir: Z'yi kaldırıp geç
     */
    gecisTipi: 'Direkt' | 'Yumusak' | 'ZKaldir' = 'Yumusak';

    /** Stay-down aktif mi (Z'yi az kaldır) */
    stayDownEtkin: boolean = true;

    /** Stay-down maksimum mesafe (mm) */
    stayDownMesafe: number = 20;

    /** Kaldırma mesafesi (mm) - stay-down aşıldığında */
    kaldirmaMesafesi: number = 2;

    // ═══════════════════════════════════════════════════════════════════
    // OVERLAP & UZATMA
    // ═══════════════════════════════════════════════════════════════════

    /** Overlap yüzdesi (%) */
    overlap: number = 10;

    /** Duvar overlap (mm) - duvar finişte ekstra geçiş */
    duvarOverlap: number = 2;

    // ═══════════════════════════════════════════════════════════════════
    // G-CODE ÜRETİMİ
    // ═══════════════════════════════════════════════════════════════════

    /** Üretilen G-code satırları */
    private gcodeBuffer: string[] = [];

    /** G-code numaralama */
    private gcodeN: number = 10;

    // ═══════════════════════════════════════════════════════════════════
    // CONSTRUCTOR
    // ═══════════════════════════════════════════════════════════════════

    constructor(public override sahne?: FikoSahne, isim?: string) {
        super(sahne);

        // Varsayılan takım (10mm çap)
        this.takim = new FikoTakimFrezeleme(this.sahne, 10);

        // Takım yolu objesi oluştur
        this.takimYolu = new FikoTakimYolu(this.sahne);
        this.add(this.takimYolu);

        // UI Ayarları
        this.userData.FikoArayuz = this.userData.FikoArayuz || {};
        this.userData.FikoArayuz.agactaGorunur = true;
        this.userData.FikoArayuz.listeGoster = true;
        this.userData.FikoArayuz.simge = '/simgeler/cam-cep.svg';
        this.userData.FikoArayuz.renk = '#00ff88';
        this.userData.FikoArayuz.altSimge = 'icon-pocket';

        // İsimlendirme
        if (!isim) {
            this.name = 'Cep.' + FikoAktiviteCep.SAYAC++;
        } else {
            this.name = isim;
        }

        // Sağ tık menüsü
        this.userData.sagMenu = [
            { adi: 'Düzenle', metot: () => this.duzenle(), simge: '✏️' },
            { adi: 'Hesapla', metot: () => this.hesapla(), simge: '⚙️' },
            { adi: 'G-Code Oluştur', metot: () => this.gcodeOlustur(), simge: '📄' },
            { adi: 'Simüle Et', metot: () => this.simuleEt(), simge: '▶️' },
            { adi: '---' },
            { adi: 'Kopyala', metot: () => this.kopyala(), simge: '📋' },
            { adi: 'Sil', metot: () => this.sil(), simge: '🗑️' }
        ];

        // Araç çubuğu (sol panel)
        this.userData.aracCubugu = [
            { adi: 'Düzenle', metot: () => this.duzenle(), simge: '✏️' },
            { adi: 'Hesapla', metot: () => this.hesapla(), simge: '⚙️' }
        ];
    }

    // ═══════════════════════════════════════════════════════════════════
    // TEMEL METODLAR
    // ═══════════════════════════════════════════════════════════════════

    /** Dialog açılma durumu - çift tıklama koruması */
    private _dialogAciliyor = false;
    /** Açık dialog referansı */
    private _acikDialog: any;

    /**
     * Aktivite düzenleme dialogunu aç
     */
    async duzenle($event?: any): Promise<void> {
        if (this._dialogAciliyor) {
            return;
        }

        if (!this.sahne?.anaGosterim?.DialogFiko) {
            console.warn('[FikoAktiviteCep] DialogFiko servisi bulunamadı');
            return;
        }

        const dialogId = 'fiko-cep-edit-' + this.uuid;
        const dialogService = this.sahne.anaGosterim.DialogFiko;

        // Mevcut açık dialog varsa öne getir
        if (this._acikDialog && this._acikDialog.miAcik) {
            const dialogEl = document.getElementById(this._acikDialog.id_dialog);
            if (dialogEl) {
                dialogEl.style.setProperty('z-index', '99999', 'important');
                dialogEl.parentElement?.style.setProperty('z-index', '99999', 'important');
            }
            return;
        }

        // ID ile mevcut dialog kontrol et
        const mevcutDialog = dialogService.idIleAl?.(dialogId);
        if (mevcutDialog && mevcutDialog.miAcik) {
            this._acikDialog = mevcutDialog;
            return;
        }

        this._acikDialog = undefined;
        this._dialogAciliyor = true;

        try {
            // Dialog component'i dynamic import et
            const { DialogFikoCepComponent } = await import('./dialog-fiko-cep/dialog-fiko-cep.component');

            const dialog = dialogService.ac({
                bilesen: DialogFikoCepComponent,
                id: dialogId,
                $event: $event,
                ayarlar: {
                    baslik: 'Cep Düzenle: ' + this.name,
                    simge: 'expand',
                    modalMi: false,
                    sinirlandi: false,
                    boyut: { w: 360, h: 'auto' }
                },
                parametreler: {
                    aktivite: this,
                    anaGosterim: this.sahne.anaGosterim,
                    sahne: this.sahne
                }
            });

            this._acikDialog = dialog;

            // Dialog kapanınca referansı temizle
            if (dialog?.onDialogClose) {
                dialog.onDialogClose.subscribe(() => {
                    this._acikDialog = undefined;
                });
            }
        } catch (e) {
            console.error('[FikoAktiviteCep] Dialog açma hatası:', e);
        } finally {
            this._dialogAciliyor = false;
        }
    }

    /**
     * Çift tıklama - düzenleme aç
     */
    ciftTiklandi(): void {
        this.duzenle();
    }

    /**
     * Aktiviteyi sil
     */
    sil(): void {
        // Takım yolu temizle
        this.takimYoluNoktalar = [];

        // Takım yolu children temizle
        if (this.takimYolu) {
            while (this.takimYolu.children.length > 0) {
                this.takimYolu.remove(this.takimYolu.children[0]);
            }
        }

        // Parent'tan kaldır
        if (this.parent) {
            this.parent.remove(this);
        }

        // Sahneyi güncelle
        this.sahne?.FikoYT?.degisti?.emit();
    }

    /**
     * Aktiviteyi kopyala
     */
    kopyala(): FikoAktiviteCep {
        const kopya = new FikoAktiviteCep(this.sahne);

        // Geometri kopyala
        kopya.cepSinirNoktalar = this.cepSinirNoktalar.map(n => n.clone());
        kopya.adalar = this.adalar.map(ada => ada.map(n => n.clone()));

        // Parametreleri kopyala
        Object.assign(kopya, {
            ustZ: this.ustZ,
            altZ: this.altZ,
            guvenliZ: this.guvenliZ,
            araGecisZ: this.araGecisZ,
            zHatve: this.zHatve,
            temizlemeStratejisi: this.temizlemeStratejisi,
            offsetYonu: this.offsetYonu,
            yanalHatveOrani: this.yanalHatveOrani,
            rasterAcisi: this.rasterAcisi,
            frezeYonu: this.frezeYonu,
            girisYontemi: this.girisYontemi,
            // ... diğer parametreler
        });

        return kopya;
    }

    // ═══════════════════════════════════════════════════════════════════
    // ANA HESAPLAMA
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Ana hesaplama - Takım yolunu hesapla
     *
     * Adımlar:
     * 1. Geometri kontrolü
     * 2. Z seviyeleri hesapla
     * 3. Offset yolları hesapla (temizleme stratejisine göre)
     * 4. Giriş hareketi ekle
     * 5. Her Z seviyesi için işleme
     * 6. Finiş geçişleri
     * 7. Çıkış hareketi
     */
    hesapla(): void {
        console.log('[FikoCAM-Cep] ═══════════════════════════════════════════════════');
        console.log('[FikoCAM-Cep] Hesaplama başlıyor...');
        console.log('[FikoCAM-Cep] Strateji:', this.temizlemeStratejisi);
        console.log('[FikoCAM-Cep] Takım çapı:', this.takim.cap, 'mm');
        console.log('[FikoCAM-Cep] Z Hatve:', this.zHatve, 'mm');

        // Temizle
        this.takimYoluNoktalar = [];
        this.offsetYollari = [];
        this.gcodeBuffer = [];

        // Geometri kontrolü
        if (this.cepSinirNoktalar.length < 3) {
            console.warn('[FikoCAM-Cep] Cep sınırı yetersiz (min 3 nokta)');
            return;
        }

        // Z seviyeleri hesapla
        const zSeviyeleri = this.hesaplaZSeviyeleri();
        console.log('[FikoCAM-Cep] Z seviyeleri:', zSeviyeleri);

        // Offset yollarını hesapla (temizleme stratejisine göre)
        this.hesaplaOffsetYollari();
        console.log('[FikoCAM-Cep] Offset yol sayısı:', this.offsetYollari.length);

        // Takım yolu oluştur
        this.olusturTakimYolu(zSeviyeleri);

        // Görsel güncelle
        this.guncelleTakimYoluGorsel();

        console.log('[FikoCAM-Cep] Hesaplama tamamlandı:', this.takimYoluNoktalar.length, 'nokta');
        console.log('[FikoCAM-Cep] ═══════════════════════════════════════════════════');
    }

    // ═══════════════════════════════════════════════════════════════════
    // Z SEVİYELERİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Z seviyelerini hesapla
     */
    private hesaplaZSeviyeleri(): number[] {
        const seviyeler: number[] = [];
        let z = this.ustZ;

        // Finiş payını hesaba kat
        const hedefZ = this.altZ + (this.finisEtkin && !this.finisAyriZ ? this.tabanFinisPay : 0);

        while (z > hedefZ) {
            z -= this.zHatve;
            if (z < hedefZ) {
                z = hedefZ;
            }
            seviyeler.push(z);
        }

        // En az bir seviye
        if (seviyeler.length === 0) {
            seviyeler.push(hedefZ);
        }

        return seviyeler;
    }

    // ═══════════════════════════════════════════════════════════════════
    // OFFSET YOLLARI HESAPLAMA
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Temizleme stratejisine göre offset yollarını hesapla
     */
    private hesaplaOffsetYollari(): void {
        this.offsetYollariHiz = []; // Adaptive dışı stratejilerde sabit feed
        this.offsetYollariArk = []; // OCCT G02/G03 dışında linear
        switch (this.temizlemeStratejisi) {
            case 'Offset':
                this.hesaplaOffsetStratejisi();
                break;
            case 'Spiral':
                this.hesaplaSpiralStratejisi();
                break;
            case 'Raster':
                this.hesaplaRasterStratejisi();
                break;
            case 'Adaptive':
                this.hesaplaAdaptiveStratejisi();
                break;
        }
    }

    /**
     * Offset (Paralel) stratejisi
     * İçten dışa veya dıştan içe paralel yollar
     */
    private hesaplaOffsetStratejisi(): void {
        // OCCT EXACT offset (yay-korumalı, Clipper'sız) — adasız pocket + shape cache'liyse.
        // Başarısızsa (shape yok / adalı / hata) Clipper offset'e düşer.
        const occt = this.occtOffsetYollari();
        if (occt) { this.offsetYollari = occt; this.offsetYollariHiz = []; return; }

        const yollar: FikoVektor3D[][] = [];

        // İlk offset: takım yarıçapı + finiş payı
        const ilkOffset = -(this.takim.cap / 2 + (this.finisEtkin ? this.finisPay : 0));

        // İlk yolu hesapla
        let mevcutYol = this.offsetUygula(this.cepSinirNoktalar, ilkOffset);

        if (mevcutYol.length < 3) {
            console.warn('[FikoCAM-Cep] İlk offset başarısız');
            return;
        }

        yollar.push(mevcutYol);

        // Daha fazla offset yolları ekle
        let adim = 1;
        const maxAdim = 1000; // Sonsuz döngü koruması
        const alanHesap = (yol: FikoVektor3D[]) => { let a = 0; for (let i = 0, n = yol.length; i < n; i++) { const j = (i + 1) % n; a += yol[i].x * yol[j].y - yol[j].x * yol[i].y; } return Math.abs(a / 2); };
        let oncekiAlan = alanHesap(mevcutYol);

        while (adim < maxAdim) {
            const offset = ilkOffset - (adim * this.yanalHatve);
            const yeniYol = this.offsetUygula(this.cepSinirNoktalar, offset);

            if (yeniYol.length < 3) {
                break; // Artık yer kalmadı
            }
            // İçe offset her adımda KÜÇÜLMELİ — küçülmüyorsa offset başarısız/aynı (üst üste binen halka üretme)
            const yeniAlan = alanHesap(yeniYol);
            if (yeniAlan >= oncekiAlan - 1e-6) break;
            oncekiAlan = yeniAlan;

            yollar.push(yeniYol);
            adim++;
        }

        // Yön kontrolü (IctenDisa için ters çevir)
        if (this.offsetYonu === 'IctenDisa') {
            yollar.reverse();
        }

        this.offsetYollari = yollar;
        console.log(`[FikoCAM-Cep] Offset: ${yollar.length} yol hesaplandı`);
    }

    /**
     * OCCT EXACT offset yolları: yüz konturunu kümülatif delta'larla OCCT ile offset'ler
     * (yay-korumalı). Senkron (shape pick'te cache'lendi). MODEL→WORLD matrisle çevrilir.
     * Sadece adasız pocket + OCCT referansı + shape cache varsa; yoksa null → Clipper.
     */
    private occtOffsetYollari(): FikoVektor3D[][] | null {
        if (!this.occtGrup || this.occtFaceId == null || !this.occtMatris) return null;
        if (this.adalar && this.adalar.length > 0) return null; // adalı pocket → Clipper (ada kaçınma)

        const cap = this.takim.cap;
        const ilkOffset = -(cap / 2 + (this.finisEtkin ? this.finisPay : 0));
        const m = this.occtMatris;
        const _v = new THREE.Vector3();
        const toWorld = (p: { x: number; y: number; z: number }) => { _v.set(p.x, p.y, p.z).applyMatrix4(m); return new FikoVektor3D(_v.x, _v.y, _v.z); };
        const toWorldXYZ = (x: number, y: number, z: number) => { _v.set(x, y, z).applyMatrix4(m); return { x: _v.x, y: _v.y, z: _v.z }; };

        const yollar: FikoVektor3D[][] = [];
        const arklar: ({ merkez: { x: number; y: number; z: number }; cw: boolean } | null)[][] = [];

        const ringEkle = (delta: number): boolean => {
            if (this.g02g03Aktif) {
                // SEGMENT (yay-korumalı) — G02/G03 için
                const segler = occtKonturOffsetSegSync(this.occtGrup, this.occtFaceId, delta);
                if (!segler?.length) return false;
                const pts: FikoVektor3D[] = []; const ark: ({ merkez: { x: number; y: number; z: number }; cw: boolean } | null)[] = [];
                for (const seg of segler) {
                    const ps = seg.noktalar; if (!ps?.length) continue;
                    if (pts.length === 0) { pts.push(toWorld(ps[0])); ark.push(null); }
                    if (seg.tip === 'arc' && seg.arc) {
                        pts.push(toWorld(ps[ps.length - 1]));
                        ark.push({ merkez: toWorldXYZ(seg.arc.cx, seg.arc.cy, seg.arc.cz), cw: seg.arc.cw });
                    } else {
                        for (let k = 1; k < ps.length; k++) { pts.push(toWorld(ps[k])); ark.push(null); }
                    }
                }
                if (pts.length < 3) return false;
                yollar.push(pts); arklar.push(ark);
                return true;
            } else {
                // Tessellated (G01) — mevcut güvenli yol
                const tpts = occtKonturOffsetSync(this.occtGrup, this.occtFaceId, delta);
                if (!tpts || tpts.length < 3) return false;
                yollar.push(tpts.map(toWorld)); arklar.push(tpts.map(() => null));
                return true;
            }
        };

        if (!ringEkle(ilkOffset)) return null; // shape yok / offset boş → Clipper fallback
        let adim = 1;
        const maxAdim = 1000;
        while (adim < maxAdim) { if (!ringEkle(ilkOffset - adim * this.yanalHatve)) break; adim++; }

        if (this.offsetYonu === 'IctenDisa') { yollar.reverse(); arklar.reverse(); }
        this.offsetYollariArk = arklar;
        console.log(`[OCCT-CAM] EXACT offset: ${yollar.length} ring${this.g02g03Aktif ? ' (G02/G03 yay)' : ''}`);
        return yollar.length ? yollar : null;
    }

    /**
     * Spiral stratejisi (sınır-takipli morph spiral)
     * Offset halkalarını tek sürekli yola bağlar — dairesel yaklaşımdan farklı
     * olarak konkav/dikdörtgen/ada içeren ceplerde de sınırı doğru takip eder.
     */
    private hesaplaSpiralStratejisi(): void {
        const halkalar: FikoVektor3D[][] = [];
        const ilkOffset = -(this.takim.cap / 2 + (this.finisEtkin ? this.finisPay : 0));

        let mevcutYol = this.offsetUygula(this.cepSinirNoktalar, ilkOffset);
        if (mevcutYol.length < 3) { this.hesaplaOffsetStratejisi(); return; }
        halkalar.push(mevcutYol);

        let adim = 1;
        const maxAdim = 1000;
        const alanH = (yol: FikoVektor3D[]) => { let a = 0; for (let i = 0, n = yol.length; i < n; i++) { const j = (i + 1) % n; a += yol[i].x * yol[j].y - yol[j].x * yol[i].y; } return Math.abs(a / 2); };
        let oncekiA = alanH(mevcutYol);
        while (adim < maxAdim) {
            const offset = ilkOffset - (adim * this.yanalHatve);
            const yeni = this.offsetUygula(this.cepSinirNoktalar, offset);
            if (yeni.length < 3) break;
            const yA = alanH(yeni);
            if (yA >= oncekiA - 1e-6) break;   // içe offset küçülmüyorsa dur (üst üste halka önle)
            oncekiA = yA;
            halkalar.push(yeni);
            adim++;
        }

        // Dıştan içe sürekli spiral (saf util — test edilebilir)
        const spiralXY = halkalariSpiraleBagla(halkalar);
        const spiral = spiralXY.map(p => new FikoVektor3D(p.x, p.y, 0));

        if (spiral.length > 2) this.offsetYollari = [spiral];
        else this.hesaplaOffsetStratejisi();
    }

    /**
     * Raster (Zik-Zak) stratejisi
     * Paralel çizgiler boyunca hareket
     */
    private hesaplaRasterStratejisi(): void {
        const yollar: FikoVektor3D[][] = [];

        // Sınır kutusunu bul
        const bbox = this.hesaplaBoundingBox();
        if (!bbox) return;

        // Raster açısına göre döndür
        const radyan = (this.rasterAcisi * Math.PI) / 180;
        const cosA = Math.cos(radyan);
        const sinA = Math.sin(radyan);

        // Takım yarıçapı offset
        const offset = this.takim.cap / 2 + (this.finisEtkin ? this.finisPay : 0);

        // Raster çizgileri oluştur
        const genislik = bbox.max.x - bbox.min.x + offset * 2;
        const yukseklik = bbox.max.y - bbox.min.y + offset * 2;
        const diyagonal = Math.sqrt(genislik * genislik + yukseklik * yukseklik);

        let y = -diyagonal / 2;
        let yon = 1;

        while (y < diyagonal / 2) {
            // Çizgi başlangıç ve bitiş
            const x1 = -diyagonal / 2;
            const x2 = diyagonal / 2;

            // Döndür ve merkeze taşı
            const merkez = this.hesaplaMerkez();
            const p1 = new FikoVektor3D(
                merkez.x + x1 * cosA - y * sinA,
                merkez.y + x1 * sinA + y * cosA,
                0
            );
            const p2 = new FikoVektor3D(
                merkez.x + x2 * cosA - y * sinA,
                merkez.y + x2 * sinA + y * cosA,
                0
            );

            // Sınır ile kesişim noktalarını bul ve çizgi ekle
            const kesisimler = this.cizgiSinirKesisim(p1, p2);
            if (kesisimler.length >= 2) {
                if (yon > 0) {
                    yollar.push(kesisimler);
                } else {
                    yollar.push(kesisimler.reverse());
                }

                if (this.rasterCiftYon) {
                    yon *= -1; // Zik-zak için yön değiştir
                }
            }

            y += this.yanalHatve;
        }

        this.offsetYollari = yollar;
    }

    /**
     * Adaptive (Dinamik) stratejisi
     * Sabit talaş yükü ile akıllı yol
     */
    private hesaplaAdaptiveStratejisi(): void {
        try {
            const sinir = this.cepSinirNoktalar.map(n => new THREE.Vector2(n.x, n.y));
            if (sinir.length < 3) { this.hesaplaOffsetStratejisi(); return; }

            const adalar = (this.adalar || []).map(ada => ada.map(n => new THREE.Vector2(n.x, n.y)));
            const cap = this.takim.cap;

            const params: AdaptiveParams = {
                optimalLoad: 0.15,
                toolDiameter: cap,
                stepover: this.yanalHatve > 0 ? this.yanalHatve : cap * 0.5,
                stockToLeave: this.finisEtkin ? this.finisPay : 0,
                trochoidalMode: this.trochoidalEtkin,
                trochoidalDiameter: this.trochoidalCap > 0 ? this.trochoidalCap : cap * 0.6
            };

            const segmentler = generateAdaptiveToolpath(sinir, params, adalar);
            if (!segmentler || segmentler.length === 0) {
                console.warn('[FikoCAM-Cep] Adaptive boş sonuç, offset fallback');
                this.offsetYollariHiz = [];
                this.hesaplaOffsetStratejisi();
                return;
            }

            const { yollar, hizlar } = this.adaptiveSegmentleriYollaraDonustur(segmentler);
            if (yollar.length === 0) { this.offsetYollariHiz = []; this.hesaplaOffsetStratejisi(); return; }

            // GÜVENLİK (savunma katmanı): adaptive yol cep sınırını taşıyorsa (gouging) offset'e düş.
            // offsetPolygon winding düzeltmesi sonrası normalde sınır içinde kalır;
            // bu guard karmaşık cep/ada edge-case'lerine karşı emniyet.
            const bb = this.hesaplaBoundingBox();
            if (bb) {
                const r = this.takim.cap / 2;
                const tasti = yollar.some(yol => yol.some(p =>
                    p.x < bb.min.x - r || p.x > bb.max.x + r ||
                    p.y < bb.min.y - r || p.y > bb.max.y + r));
                if (tasti) {
                    console.warn('[FikoCAM-Cep] Adaptive sınır dışı taştı → offset fallback (güvenlik)');
                    this.offsetYollariHiz = [];
                    this.hesaplaOffsetStratejisi();
                    return;
                }
            }

            this.offsetYollari = yollar;
            this.offsetYollariHiz = hizlar; // değişken feed (engagement bazlı)
            console.log(`[FikoCAM-Cep] Adaptive: ${segmentler.length} segment → ${yollar.length} yol (değişken feed)`);
        } catch (e) {
            console.warn('[FikoCAM-Cep] Adaptive hata, offset fallback:', e);
            this.offsetYollariHiz = [];
            this.hesaplaOffsetStratejisi();
        }
    }

    /**
     * Adaptive ToolpathSegment[] → offsetYollari + paralel feed çarpanları.
     * Süreksizlikte yeni yol başlatır; arc/trochoidal segmentleri tessela eder.
     * Her noktaya segment'in feedrate çarpanı (engagement bazlı) atanır.
     */
    private adaptiveSegmentleriYollaraDonustur(segmentler: ToolpathSegment[]): { yollar: FikoVektor3D[][]; hizlar: number[][] } {
        const { yollar, hizlar } = segmentleriYollara(segmentler as any);
        return { yollar: yollar.map(y => y.map(p => new FikoVektor3D(p.x, p.y, 0))), hizlar };
    }

    // ═══════════════════════════════════════════════════════════════════
    // GEOMETRİ YARDIMCI METODLARI
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Poligona offset uygula
     */
    private offsetUygula(noktalar: FikoVektor3D[], offset: number): FikoVektor3D[] {
        try {
            // kapali=true ZORUNLU: cep sınırı tekrarsız köşe listesi → oto-tespit "açık" sanıp
            // iki taraflı bant üretiyordu / negatif offset boş dönüyordu (taşma + bozuk yol kökü).
            const result = offsetHat(noktalar, offset, 'Dairesel', 0.01, true);
            if (result && result.length >= 3) {
                return result.map((p: any) => new FikoVektor3D(p.x, p.y, 0));
            }
        } catch (e) {
            console.warn('[FikoCAM-Cep] Offset hatası:', e);
        }
        return [];
    }

    /**
     * Merkez noktayı hesapla
     */
    private hesaplaMerkez(): FikoVektor3D {
        let toplamX = 0, toplamY = 0;
        for (const n of this.cepSinirNoktalar) {
            toplamX += n.x;
            toplamY += n.y;
        }
        const count = this.cepSinirNoktalar.length;
        return new FikoVektor3D(toplamX / count, toplamY / count, 0);
    }

    /**
     * Bounding box hesapla
     */
    private hesaplaBoundingBox(): { min: FikoVektor3D, max: FikoVektor3D } | null {
        if (this.cepSinirNoktalar.length === 0) return null;

        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        for (const n of this.cepSinirNoktalar) {
            if (n.x < minX) minX = n.x;
            if (n.y < minY) minY = n.y;
            if (n.x > maxX) maxX = n.x;
            if (n.y > maxY) maxY = n.y;
        }

        return {
            min: new FikoVektor3D(minX, minY, 0),
            max: new FikoVektor3D(maxX, maxY, 0)
        };
    }

    /**
     * Nokta sınır içinde mi
     */
    private noktaSinirIcindeMi(nokta: FikoVektor3D): boolean {
        // Ray casting algoritması
        let icinde = false;
        const n = this.cepSinirNoktalar.length;

        for (let i = 0, j = n - 1; i < n; j = i++) {
            const xi = this.cepSinirNoktalar[i].x;
            const yi = this.cepSinirNoktalar[i].y;
            const xj = this.cepSinirNoktalar[j].x;
            const yj = this.cepSinirNoktalar[j].y;

            if (((yi > nokta.y) !== (yj > nokta.y)) &&
                (nokta.x < (xj - xi) * (nokta.y - yi) / (yj - yi) + xi)) {
                icinde = !icinde;
            }
        }

        return icinde;
    }

    /**
     * Çizgi-sınır kesişim noktaları
     */
    private cizgiSinirKesisim(p1: FikoVektor3D, p2: FikoVektor3D): FikoVektor3D[] {
        const kesisimler: FikoVektor3D[] = [];
        const n = this.cepSinirNoktalar.length;

        for (let i = 0; i < n; i++) {
            const a = this.cepSinirNoktalar[i];
            const b = this.cepSinirNoktalar[(i + 1) % n];

            const kesisim = this.cizgiCizgiKesisim(p1, p2, a, b);
            if (kesisim) {
                kesisimler.push(kesisim);
            }
        }

        // X veya Y'ye göre sırala
        kesisimler.sort((a, b) => {
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            if (Math.abs(dx) > Math.abs(dy)) {
                return a.x - b.x;
            }
            return a.y - b.y;
        });

        return kesisimler;
    }

    /**
     * İki çizgi kesişimi
     */
    private cizgiCizgiKesisim(p1: FikoVektor3D, p2: FikoVektor3D, p3: FikoVektor3D, p4: FikoVektor3D): FikoVektor3D | null {
        const d = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
        if (Math.abs(d) < 0.0001) return null;

        const t = ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / d;
        const u = -((p1.x - p2.x) * (p1.y - p3.y) - (p1.y - p2.y) * (p1.x - p3.x)) / d;

        if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
            return new FikoVektor3D(
                p1.x + t * (p2.x - p1.x),
                p1.y + t * (p2.y - p1.y),
                0
            );
        }

        return null;
    }

    // ═══════════════════════════════════════════════════════════════════
    // TAKIM YOLU OLUŞTURMA
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Ana takım yolunu oluştur
     */
    private olusturTakimYolu(zSeviyeleri: number[]): void {
        if (this.offsetYollari.length === 0) {
            console.warn('[FikoCAM-Cep] Offset yolu yok');
            return;
        }

        // Rapid - güvenli Z'ye
        this.ekleRapid(this.offsetYollari[0][0].x, this.offsetYollari[0][0].y, this.guvenliZ);

        // Her Z seviyesi için
        for (let zIdx = 0; zIdx < zSeviyeleri.length; zIdx++) {
            const z = zSeviyeleri[zIdx];
            const oncekiZ = zIdx === 0 ? this.ustZ : zSeviyeleri[zIdx - 1];

            console.log(`[FikoCAM-Cep] Z seviyesi ${zIdx + 1}/${zSeviyeleri.length}: ${z.toFixed(2)}mm`);

            // Giriş hareketi (ilk seviyede)
            if (zIdx === 0) {
                this.girisHareketEkle(z, oncekiZ);
            } else {
                // Sonraki seviyelere geçiş
                this.seviyeGecisEkle(z, oncekiZ);
            }

            // Offset yollarını işle
            this.offsetYollariIsle(z);
        }

        // Finiş geçişi (varsa)
        if (this.finisEtkin) {
            this.finisGecisiEkle();
        }

        // Çıkış hareketi
        this.cikisHareketEkle();
    }

    /**
     * Offset yollarını işle
     */
    private offsetYollariIsle(z: number): void {
        for (let yolIdx = 0; yolIdx < this.offsetYollari.length; yolIdx++) {
            const yol = this.offsetYollari[yolIdx];

            if (yol.length < 2) continue;

            const hizYol = this.offsetYollariHiz[yolIdx]; // Adaptive değişken feed (yoksa undefined → 1)
            const arkYol = this.offsetYollariArk[yolIdx]; // OCCT G02/G03 yay bilgisi (yoksa Clipper → linear)
            const occtRing = !!arkYol;                    // OCCT ring zaten kapalı (son nokta == ilk)

            // İlk noktaya git
            if (yolIdx === 0) {
                // Zaten giriş yapıldı
            } else {
                // Yollar arası geçiş
                this.yolGecisEkle(yol[0], z);
            }

            // Yol boyunca ilerle (yay varsa G02/G03, yoksa linear)
            for (let i = 0; i < yol.length; i++) {
                const n = yol[i];
                const ark = arkYol?.[i];
                if (ark && i > 0) {
                    this.ekleArc(yol[i - 1], n, ark.merkez, ark.cw, z, hizYol?.[i] ?? 1);
                } else {
                    this.ekleFeed(n.x, n.y, z, hizYol?.[i] ?? 1);
                }
            }

            // Kapalı yol - başa dön (OCCT ring zaten kapalı, Clipper'da kapat)
            if (!occtRing && yol.length > 2) {
                this.ekleFeed(yol[0].x, yol[0].y, z, hizYol?.[0] ?? 1);
            }
        }
    }

    /** Yay (G02/G03) hareket noktası ekler. merkez/cw XY düzlemi varsayımı (I/J). */
    private ekleArc(baslangic: FikoVektor3D, bitis: { x: number; y: number; z: number }, merkez: { x: number; y: number; z: number }, cw: boolean, z: number, hizCarpan: number = 1): void {
        const nokta = new FikoVektorTakimYol3D(bitis.x, bitis.y, z);
        nokta.tip = cw ? 'arc_cw' : 'arc_ccw';
        nokta.i = merkez.x - baslangic.x;   // I = merkez.x - başlangıç.x (incremental)
        nokta.j = merkez.y - baslangic.y;   // J = merkez.y - başlangıç.y
        nokta.arcI = nokta.i; nokta.arcJ = nokta.j; nokta.arcCW = cw;
        nokta.hiz = this.ilerleme * hizCarpan;
        nokta.devir = this.devir;
        nokta.renk = new THREE.Color(0x00ccff); // açık mavi - yay
        this.takimYoluNoktalar.push(nokta);
    }

    // ═══════════════════════════════════════════════════════════════════
    // GİRİŞ HAREKETLERİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Giriş hareketi ekle
     */
    private girisHareketEkle(hedefZ: number, baslangicZ: number): void {
        const ilkNokta = this.offsetYollari[0]?.[0];
        if (!ilkNokta) return;

        switch (this.girisYontemi) {
            case 'Dikey':
                this.girisHareketDikey(ilkNokta, hedefZ);
                break;
            case 'Rampa':
                this.girisHareketRampa(ilkNokta, hedefZ, baslangicZ);
                break;
            case 'Helisel':
                this.girisHareketHelisel(ilkNokta, hedefZ, baslangicZ);
                break;
            case 'Yay':
                this.girisHareketYay(ilkNokta, hedefZ);
                break;
            case 'OnDelme':
                this.girisHareketOnDelme(hedefZ);
                break;
        }
    }

    /**
     * Dikey giriş (Plunge)
     */
    private girisHareketDikey(nokta: FikoVektor3D, hedefZ: number): void {
        // Rapid üzerine gel
        this.ekleRapid(nokta.x, nokta.y, this.araGecisZ);
        // Dalma hızıyla Z'ye in
        this.ekleDalma(nokta.x, nokta.y, hedefZ);
    }

    /**
     * Rampa giriş
     */
    private girisHareketRampa(nokta: FikoVektor3D, hedefZ: number, baslangicZ: number): void {
        const derinlik = baslangicZ - hedefZ;
        const rampaUzunluk = Math.max(this.rampaMinUzunluk, derinlik / Math.tan(this.rampaAcisi * Math.PI / 180));

        // İlk yolun başlangıcından geriye doğru rampa başlangıcı
        const yol = this.offsetYollari[0];
        if (yol.length < 2) {
            this.girisHareketDikey(nokta, hedefZ);
            return;
        }

        // Yön vektörü
        const dx = yol[1].x - yol[0].x;
        const dy = yol[1].y - yol[0].y;
        const len = Math.sqrt(dx * dx + dy * dy);
        const ux = -dx / len;
        const uy = -dy / len;

        // Rampa başlangıç noktası
        const rampaBasX = nokta.x + ux * rampaUzunluk;
        const rampaBasY = nokta.y + uy * rampaUzunluk;

        // Rapid rampa başlangıcına
        this.ekleRapid(rampaBasX, rampaBasY, baslangicZ);

        // Rampa - açılı dalma
        const adimSayisi = Math.ceil(rampaUzunluk / 2);
        const zAdim = derinlik / adimSayisi;
        const xyAdim = rampaUzunluk / adimSayisi;

        for (let i = 1; i <= adimSayisi; i++) {
            const x = rampaBasX - ux * (xyAdim * i);
            const y = rampaBasY - uy * (xyAdim * i);
            const z = baslangicZ - (zAdim * i);
            this.ekleFeed(x, y, z);
        }
    }

    /**
     * Helisel giriş (Spiral)
     */
    private girisHareketHelisel(nokta: FikoVektor3D, hedefZ: number, baslangicZ: number): void {
        const derinlik = baslangicZ - hedefZ;
        const turSayisi = Math.ceil(derinlik / this.heliselAdim);
        const radius = Math.min(this.heliselCap / 2, this.takim.cap * 0.4);

        // Merkez - ilk nokta
        const merkezX = nokta.x;
        const merkezY = nokta.y;

        // Rapid spiral başlangıcına
        this.ekleRapid(merkezX + radius, merkezY, baslangicZ);

        // Spiral dalma
        const toplamAci = turSayisi * 2 * Math.PI;
        const adimSayisi = Math.ceil(toplamAci / 0.1); // 0.1 radyan adımlar
        const aciAdim = toplamAci / adimSayisi;
        const zAdim = derinlik / adimSayisi;

        for (let i = 1; i <= adimSayisi; i++) {
            const aci = i * aciAdim;
            const x = merkezX + radius * Math.cos(aci);
            const y = merkezY + radius * Math.sin(aci);
            const z = baslangicZ - (i * zAdim);
            this.ekleFeed(x, y, z);
        }

        // Merkeze gel
        this.ekleFeed(merkezX, merkezY, hedefZ);
    }

    /**
     * Yay giriş (Arc Roll-in)
     */
    private girisHareketYay(nokta: FikoVektor3D, hedefZ: number): void {
        // Yol yönünü bul
        const yol = this.offsetYollari[0];
        if (yol.length < 2) {
            this.girisHareketDikey(nokta, hedefZ);
            return;
        }

        const dx = yol[1].x - yol[0].x;
        const dy = yol[1].y - yol[0].y;
        const len = Math.sqrt(dx * dx + dy * dy);

        // Normal vektör (sola dönük)
        const nx = -dy / len;
        const ny = dx / len;

        // Yay merkezi ve başlangıcı
        const arcMerkezX = nokta.x + nx * this.yayRadius;
        const arcMerkezY = nokta.y + ny * this.yayRadius;
        const arcBasX = arcMerkezX + nx * this.yayRadius;
        const arcBasY = arcMerkezY + ny * this.yayRadius;

        // Rapid ve Z
        this.ekleRapid(arcBasX, arcBasY, this.araGecisZ);
        this.ekleDalma(arcBasX, arcBasY, hedefZ);

        // Yay hareketi (G02/G03)
        const arcNokta = new FikoVektorTakimYol3D(nokta.x, nokta.y, hedefZ);
        arcNokta.tip = 'arc';
        arcNokta.arcI = arcMerkezX - arcBasX;
        arcNokta.arcJ = arcMerkezY - arcBasY;
        arcNokta.arcCW = this.frezeYonu === 'Tirmanma';
        arcNokta.hiz = this.ilerleme;
        arcNokta.devir = this.devir;
        arcNokta.renk = new THREE.Color(0x00ff00);
        this.takimYoluNoktalar.push(arcNokta);
    }

    /**
     * Ön-delme girişi
     */
    private girisHareketOnDelme(hedefZ: number): void {
        if (!this.onDelmeNokta) {
            console.warn('[FikoCAM-Cep] Ön-delme noktası tanımlı değil');
            this.girisHareketDikey(this.offsetYollari[0]?.[0] || new FikoVektor3D(0, 0, 0), hedefZ);
            return;
        }

        // Delik noktasına rapid
        this.ekleRapid(this.onDelmeNokta.x, this.onDelmeNokta.y, this.araGecisZ);

        // Dalma
        this.ekleDalma(this.onDelmeNokta.x, this.onDelmeNokta.y, hedefZ);

        // İlk yol noktasına feed
        const ilkNokta = this.offsetYollari[0]?.[0];
        if (ilkNokta) {
            this.ekleFeed(ilkNokta.x, ilkNokta.y, hedefZ);
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // GEÇİŞ HAREKETLERİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Z seviyesi geçişi
     */
    private seviyeGecisEkle(hedefZ: number, oncekiZ: number): void {
        const sonNokta = this.takimYoluNoktalar[this.takimYoluNoktalar.length - 1];
        if (!sonNokta) return;

        // Mevcut pozisyondan yeni Z'ye dalma
        this.ekleDalma(sonNokta.x, sonNokta.y, hedefZ);
    }

    /**
     * Yollar arası geçiş
     */
    private yolGecisEkle(hedefNokta: FikoVektor3D, z: number): void {
        const sonNokta = this.takimYoluNoktalar[this.takimYoluNoktalar.length - 1];
        if (!sonNokta) return;

        const mesafe = Math.sqrt(
            Math.pow(hedefNokta.x - sonNokta.x, 2) +
            Math.pow(hedefNokta.y - sonNokta.y, 2)
        );

        if (this.stayDownEtkin && mesafe <= this.stayDownMesafe) {
            // Stay-down: Z'yi kaldırmadan direkt git
            if (this.gecisTipi === 'Yumusak') {
                // TODO: Smooth arc geçiş
                this.ekleFeed(hedefNokta.x, hedefNokta.y, z);
            } else {
                this.ekleFeed(hedefNokta.x, hedefNokta.y, z);
            }
        } else {
            // Z'yi kaldır ve git
            const kaldirmaZ = z + this.kaldirmaMesafesi;
            this.ekleRapid(sonNokta.x, sonNokta.y, kaldirmaZ);
            this.ekleRapid(hedefNokta.x, hedefNokta.y, kaldirmaZ);
            this.ekleFeed(hedefNokta.x, hedefNokta.y, z);
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // FİNİŞ GEÇİŞİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Finiş geçişi ekle
     */
    private finisGecisiEkle(): void {
        if (!this.finisEtkin || this.finisPay <= 0) return;

        console.log('[FikoCAM-Cep] Finiş geçişi ekleniyor...');

        // Finiş Z seviyesi
        const finisZ = this.finisAyriZ ? this.finisZ : this.altZ;

        // Finiş offset yolu (sadece takım yarıçapı, finiş payı yok)
        const finisOffset = -(this.takim.cap / 2);
        const finisYol = this.offsetUygula(this.cepSinirNoktalar, finisOffset);

        if (finisYol.length < 3) {
            console.warn('[FikoCAM-Cep] Finiş yolu oluşturulamadı');
            return;
        }

        // Finiş için geçiş
        for (let gecis = 0; gecis < this.finisGecisSayisi; gecis++) {
            // İlk noktaya rapid
            this.ekleRapid(finisYol[0].x, finisYol[0].y, this.araGecisZ);
            this.ekleDalma(finisYol[0].x, finisYol[0].y, finisZ);

            // Finiş yolu - özel hız ve devir
            for (const n of finisYol) {
                const nokta = new FikoVektorTakimYol3D(n.x, n.y, finisZ);
                nokta.tip = 'feed';
                nokta.hiz = this.finisIlerleme;
                nokta.devir = this.finisDevir;
                nokta.renk = new THREE.Color(0x00ffff); // Cyan - finiş
                this.takimYoluNoktalar.push(nokta);
            }

            // Kapalı - başa dön
            const kapatma = new FikoVektorTakimYol3D(finisYol[0].x, finisYol[0].y, finisZ);
            kapatma.tip = 'feed';
            kapatma.hiz = this.finisIlerleme;
            kapatma.devir = this.finisDevir;
            kapatma.renk = new THREE.Color(0x00ffff);
            this.takimYoluNoktalar.push(kapatma);
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // ÇIKIŞ HAREKETİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Çıkış hareketi ekle
     */
    private cikisHareketEkle(): void {
        const sonNokta = this.takimYoluNoktalar[this.takimYoluNoktalar.length - 1];
        if (!sonNokta) return;

        switch (this.cikisYontemi) {
            case 'Dikey':
                this.ekleRapid(sonNokta.x, sonNokta.y, this.guvenliZ);
                break;
            case 'Rampa':
                // TODO: Rampa çıkış
                this.ekleRapid(sonNokta.x, sonNokta.y, this.guvenliZ);
                break;
            case 'Yay':
                // TODO: Arc çıkış
                this.ekleRapid(sonNokta.x, sonNokta.y, this.guvenliZ);
                break;
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // NOKTA EKLEME YARDIMCILARI
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Rapid hareket ekle
     */
    private ekleRapid(x: number, y: number, z: number): void {
        const nokta = new FikoVektorTakimYol3D(x, y, z);
        nokta.tip = 'rapid';
        nokta.hiz = this.hizRapid;
        nokta.devir = this.devir;
        nokta.renk = new THREE.Color(0xff0000); // Kırmızı - rapid
        this.takimYoluNoktalar.push(nokta);
    }

    /**
     * Feed (ilerleme) hareketi ekle
     */
    private ekleFeed(x: number, y: number, z: number, hizCarpan: number = 1): void {
        const nokta = new FikoVektorTakimYol3D(x, y, z);
        nokta.tip = 'feed';
        nokta.hiz = this.ilerleme * hizCarpan;
        nokta.devir = this.devir;
        nokta.renk = new THREE.Color(0x00ff00); // Yeşil - feed
        this.takimYoluNoktalar.push(nokta);
    }

    /**
     * Dalma hareketi ekle
     */
    private ekleDalma(x: number, y: number, z: number): void {
        const nokta = new FikoVektorTakimYol3D(x, y, z);
        nokta.tip = 'feed';
        nokta.hiz = this.ilerlemeDalma;
        nokta.devir = this.devir;
        nokta.renk = new THREE.Color(0xffff00); // Sarı - dalma
        this.takimYoluNoktalar.push(nokta);
    }

    // ═══════════════════════════════════════════════════════════════════
    // GÖRSEL GÜNCELLEME
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Takım yolu görselini güncelle
     */
    private guncelleTakimYoluGorsel(): void {
        if (!this.takimYolu) return;

        // Children temizle
        while (this.takimYolu.children.length > 0) {
            this.takimYolu.remove(this.takimYolu.children[0]);
        }

        // Noktaları ata (setter görselleştirmeyi yapar)
        this.takimYolu.noktalar = this.takimYoluNoktalar;
    }

    // ═══════════════════════════════════════════════════════════════════
    // G-CODE ÜRETİMİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * G-code oluştur
     */
    gcodeOlustur(): string {
        this.gcodeBuffer = [];
        this.gcodeN = 10;

        // Header
        this.gcodeEkle('%');
        this.gcodeEkle('O1001 (CEP ACMA)');
        this.gcodeEkle(`(Tarih: ${new Date().toLocaleDateString('tr-TR')})`);
        this.gcodeEkle(`(Takim Cap: ${this.takim.cap}mm)`);
        this.gcodeEkle(`(Derinlik: ${Math.abs(this.altZ - this.ustZ)}mm)`);
        this.gcodeEkle('');

        // Güvenlik
        this.gcodeEkle('G90 G94 G17'); // Mutlak, mm/dk, XY düzlemi
        this.gcodeEkle('G21'); // Metrik
        this.gcodeEkle(this.workOffset || 'G54'); // Work offset (G54-G59)
        this.gcodeEkle(`S${this.devir} M03`); // Spindle on
        if (this.sogutmaAktif) this.gcodeEkle('M08'); // Soğutma açık (flood)

        // Güvenli Z
        this.gcodeEkle(`G00 Z${this.guvenliZ.toFixed(3)}`);

        // Takım yolu
        for (const nokta of this.takimYoluNoktalar) {
            if (nokta.tip === 'rapid') {
                this.gcodeEkle(`G00 X${nokta.x.toFixed(3)} Y${nokta.y.toFixed(3)} Z${nokta.z.toFixed(3)}`);
            } else if (nokta.tip === 'arc' || nokta.tip === 'arc_cw' || nokta.tip === 'arc_ccw') {
                // G02 (saat yönü) / G03 (ters). tip arc_cw/arc_ccw veya arc+arcCW.
                const cw = nokta.tip === 'arc_cw' || (nokta.tip === 'arc' && nokta.arcCW);
                const i = (nokta.i ?? nokta.arcI ?? 0), j = (nokta.j ?? nokta.arcJ ?? 0);
                this.gcodeEkle(`${cw ? 'G02' : 'G03'} X${nokta.x.toFixed(3)} Y${nokta.y.toFixed(3)} I${i.toFixed(3)} J${j.toFixed(3)} F${nokta.hiz}`);
            } else {
                // feed / cut / linear
                this.gcodeEkle(`G01 X${nokta.x.toFixed(3)} Y${nokta.y.toFixed(3)} Z${nokta.z.toFixed(3)} F${nokta.hiz}`);
            }
        }

        // Footer
        this.gcodeEkle('');
        this.gcodeEkle(`G00 Z${this.guvenliZ.toFixed(3)}`);
        if (this.sogutmaAktif) this.gcodeEkle('M09'); // Soğutma kapalı
        this.gcodeEkle('M05'); // Spindle off
        this.gcodeEkle('M30'); // Program end
        this.gcodeEkle('%');

        return this.gcodeBuffer.join('\n');
    }

    /**
     * G-code satırı ekle
     */
    private gcodeEkle(satir: string): void {
        if (satir.startsWith('G') || satir.startsWith('M') || satir.startsWith('S')) {
            this.gcodeBuffer.push(`N${this.gcodeN} ${satir}`);
            this.gcodeN += 10;
        } else {
            this.gcodeBuffer.push(satir);
        }
    }

    /**
     * Simülasyon başlat
     */
    simuleEt(): void {
        console.log('[FikoCAM-Cep] Simülasyon henüz implemente edilmedi');
        // TODO: Takım animasyonu
    }

    // ═══════════════════════════════════════════════════════════════════
    // JSON SERİALİZASYON
    // ═══════════════════════════════════════════════════════════════════

    /**
     * JSON'a dönüştür
     */
    toJSON(): any {
        return {
            type: this.type,
            name: this.name,
            cepSinirNoktalar: this.cepSinirNoktalar.map(n => ({ x: n.x, y: n.y, z: n.z })),
            adalar: this.adalar.map(ada => ada.map(n => ({ x: n.x, y: n.y, z: n.z }))),

            // Z parametreleri (Heights - Profesyonel CAM)
            ustZ: this.ustZ,
            altZ: this.altZ,
            clearanceHeight: this.clearanceHeight,
            retractHeight: this.retractHeight,
            feedHeight: this.feedHeight,
            zHatve: this.zHatve,

            // Strateji
            temizlemeStratejisi: this.temizlemeStratejisi,
            offsetYonu: this.offsetYonu,
            yanalHatveOrani: this.yanalHatveOrani,
            rasterAcisi: this.rasterAcisi,
            rasterCiftYon: this.rasterCiftYon,
            frezeYonu: this.frezeYonu,
            optimalYuk: this.optimalYuk,
            trochoidalEtkin: this.trochoidalEtkin,
            trochoidalCap: this.trochoidalCap,

            // Giriş/Çıkış
            girisYontemi: this.girisYontemi,
            rampaAcisi: this.rampaAcisi,
            heliselCap: this.heliselCap,
            heliselAdim: this.heliselAdim,
            yayRadius: this.yayRadius,
            cikisYontemi: this.cikisYontemi,

            // Hızlar
            ilerleme: this.ilerleme,
            ilerlemeDalma: this.ilerlemeDalma,
            hizRapid: this.hizRapid,
            devir: this.devir,

            // Finiş & Stock to Leave
            finisEtkin: this.finisEtkin,
            stockToLeaveWall: this.stockToLeaveWall,
            stockToLeaveFloor: this.stockToLeaveFloor,
            finisGecisSayisi: this.finisGecisSayisi,
            finisIlerleme: this.finisIlerleme,
            finisDevir: this.finisDevir,

            // Köşe
            koseIsleme: this.koseIsleme,
            koseYavaslamaEtkin: this.koseYavaslamaEtkin,
            koseYavaslamaOrani: this.koseYavaslamaOrani,

            // Geçiş
            gecisTipi: this.gecisTipi,
            stayDownEtkin: this.stayDownEtkin,
            stayDownMesafe: this.stayDownMesafe,

            // Takım
            takimCap: this.takim.cap
        };
    }

    /**
     * JSON'dan oluştur
     */
    static fromJSON(json: any, sahne?: FikoSahne): FikoAktiviteCep {
        const aktivite = new FikoAktiviteCep(sahne, json.name);

        // Geometri
        if (json.cepSinirNoktalar) {
            aktivite.cepSinirNoktalar = json.cepSinirNoktalar.map((n: any) =>
                new FikoVektor3D(n.x, n.y, n.z || 0)
            );
        }
        if (json.adalar) {
            aktivite.adalar = json.adalar.map((ada: any) =>
                ada.map((n: any) => new FikoVektor3D(n.x, n.y, n.z || 0))
            );
        }

        // Parametreleri ata
        Object.assign(aktivite, {
            // Heights (Z Seviyeleri)
            ustZ: json.ustZ ?? 0,
            altZ: json.altZ ?? -10,
            clearanceHeight: json.clearanceHeight ?? json.guvenliZ ?? 25,
            retractHeight: json.retractHeight ?? json.araGecisZ ?? 5,
            feedHeight: json.feedHeight ?? 2,
            zHatve: json.zHatve ?? 2,

            // Strateji
            temizlemeStratejisi: json.temizlemeStratejisi ?? 'Offset',
            offsetYonu: json.offsetYonu ?? 'IctenDisa',
            yanalHatveOrani: json.yanalHatveOrani ?? 0.4,
            rasterAcisi: json.rasterAcisi ?? 0,
            rasterCiftYon: json.rasterCiftYon ?? true,
            frezeYonu: json.frezeYonu ?? 'Tirmanma',
            optimalYuk: json.optimalYuk ?? 0.15,
            trochoidalEtkin: json.trochoidalEtkin ?? false,
            trochoidalCap: json.trochoidalCap ?? 0,

            // Giriş/Çıkış
            girisYontemi: json.girisYontemi ?? 'Helisel',
            rampaAcisi: json.rampaAcisi ?? 3,
            heliselCap: json.heliselCap ?? 10,
            heliselAdim: json.heliselAdim ?? 1,
            yayRadius: json.yayRadius ?? 5,
            cikisYontemi: json.cikisYontemi ?? 'Dikey',

            // Hızlar
            ilerleme: json.ilerleme ?? 1000,
            ilerlemeDalma: json.ilerlemeDalma ?? 300,
            hizRapid: json.hizRapid ?? 10000,
            devir: json.devir ?? 8000,

            // Finiş & Stock to Leave
            finisEtkin: json.finisEtkin ?? true,
            stockToLeaveWall: json.stockToLeaveWall ?? json.finisPay ?? 0.3,
            stockToLeaveFloor: json.stockToLeaveFloor ?? json.tabanFinisPay ?? 0.1,
            finisGecisSayisi: json.finisGecisSayisi ?? 1,
            finisIlerleme: json.finisIlerleme ?? 800,
            finisDevir: json.finisDevir ?? 10000,

            // Köşe
            koseIsleme: json.koseIsleme ?? 'Dairesel',
            koseYavaslamaEtkin: json.koseYavaslamaEtkin ?? true,
            koseYavaslamaOrani: json.koseYavaslamaOrani ?? 50,

            // Geçiş
            gecisTipi: json.gecisTipi ?? 'Yumusak',
            stayDownEtkin: json.stayDownEtkin ?? true,
            stayDownMesafe: json.stayDownMesafe ?? 20
        });

        // Takım
        if (json.takimCap) {
            aktivite.takim = new FikoTakimFrezeleme(sahne, json.takimCap);
        }

        return aktivite;
    }
}
